/**
 * Постоянная левая колонка карточки маркетплейса.
 *
 * Опора, по которой человек понимает, ту ли карточку он правит: фото витрины,
 * лента остальных снимков, пять цифр и происхождение. Не редактируется и не
 * меняется при переключении вкладок.
 *
 * Раскладка — по утверждённому прототипу (`docs/prototype/mp-card-modal.html`).
 */

import React from 'react'
import { ImageOff, ZoomIn } from 'lucide-react'
import { cn } from '../../../lib/cn'
import { CopyButton } from '../../../components/ui/CopyButton'
import { useEntityShellLayout } from '../../../components/entity-view/layoutContext'

export interface CardSidebarStat {
  label: string
  value: React.ReactNode
  note?: string
  /** Ячейка на всю ширину: так показывается цена — главная цифра колонки. */
  wide?: boolean
  /** Приглушённый — данных нет; тревожный — есть на что смотреть. */
  tone?: 'dim' | 'bad'
}

export interface CardSidebarOrigin {
  label: string
  value: string
  href?: string
  mono?: boolean
  /** Кнопка копирования рядом со значением. */
  copy?: boolean
  /** Значение — кнопка (открыть связанное окно); при заданном `href` не используется. */
  onClick?: () => void
}

interface CardSidebarProps {
  images: string[]
  activeImage: number
  onActiveImage: (index: number) => void
  onOpenImage: (index: number) => void
  stats: CardSidebarStat[]
  origins: CardSidebarOrigin[]
  /**
   * Чем рисовать картинку. Витрина отдаёт открытые URL площадки, а черновик
   * ПИМ — свои файлы, за которыми нужен токен: обычный <img> получил бы 401.
   * Поэтому источник изображения задаётся снаружи, а раскладка остаётся одна.
   */
  renderImage?: (src: string, alt: string, className: string) => React.ReactNode
  /** Подпись под колонкой, когда фото нет: у витрины и черновика причины разные. */
  emptyImageText?: string
  /** Источник выбранного превью: печать, макет или визуал карточки. */
  imageCaption?: string
  /**
   * Что делать по «+N». Лента показывает первые несколько миниатюр, и без
   * этого обработчика счётчик выглядит кнопкой, которая ничего не делает.
   */
  onShowAll?: () => void
  /**
   * Пропорция главного изображения. У карточек площадок и черновиков — 3:4
   * (слайд витрины); у товара и пряника — квадрат: превью листа и файл печати
   * вписываются целиком на белом фоне.
   */
  aspect?: 'portrait' | 'square'
  /** Заголовок блока идентификаторов; по умолчанию «Происхождение». */
  originsTitle?: string
  /** Блок под идентификаторами: теги, пояснения. */
  footer?: React.ReactNode
}

/** Сколько миниатюр помещается в ряд колонки 19 rem, не переносясь. */
const THUMBS_SHOWN = 4

export const CardSidebar: React.FC<CardSidebarProps> = ({
  images,
  activeImage,
  onActiveImage,
  onOpenImage,
  stats,
  origins,
  renderImage,
  emptyImageText = 'На витрине нет фото',
  imageCaption,
  onShowAll,
  aspect = 'portrait',
  originsTitle = 'Происхождение',
  footer,
}) => {
  const heroAspect = aspect === 'square' ? 'aspect-square' : 'aspect-[3/4]'
  const image = (src: string, alt: string, className: string) =>
    renderImage ? renderImage(src, alt, className) : <img src={src} alt={alt} className={className} />
  // Узкое окно: колонка над вкладками — превью меньше и рядом с цифрами, цифры в 2 колонки.
  const { narrow } = useEntityShellLayout()
  const heroWidth = narrow ? 'max-w-[6.5rem]' : 'max-w-[15rem]'

  const media = (
  <div className={narrow ? 'w-[6.5rem] shrink-0 space-y-2' : 'space-y-3'}>
    {images[activeImage] ? (
      <button
        type="button"
        title="Открыть в полном размере"
        onClick={() => onOpenImage(activeImage)}
        className={cn(
          'group relative mx-auto block w-full cursor-zoom-in overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-medium dark:border-zinc-700',
          heroWidth,
          heroAspect,
        )}
      >
        {image(images[activeImage], 'Фото карточки', 'h-full w-full object-contain')}
        <span className="pointer-events-none absolute bottom-2.5 right-2.5 inline-flex items-center gap-1.5 rounded-md bg-gray-950/75 px-2.5 py-1.5 text-xs font-medium text-white opacity-0 backdrop-blur transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
          <ZoomIn className="h-3.5 w-3.5" />
          Увеличить
        </span>
      </button>
    ) : (
      <div
        className={cn(
          'mx-auto flex w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-red-200 bg-red-50/60 px-3 text-center text-xs text-red-700 dark:border-red-900 dark:bg-red-950/25 dark:text-red-300',
          heroWidth,
          heroAspect,
        )}
      >
        <ImageOff className="h-7 w-7" />
        {emptyImageText}
      </div>
    )}

    {imageCaption && images[activeImage] && <p className="text-center text-xs text-gray-500 dark:text-zinc-400">{imageCaption}</p>}

    {images.length > 1 && (
      <div className="flex flex-wrap justify-center gap-1.5">
        {images.slice(0, THUMBS_SHOWN).map((url, index) => (
          <button
            type="button"
            key={`${url}-${index}`}
            aria-current={activeImage === index}
            onClick={() => onActiveImage(index)}
            className={cn(
              'h-14 w-11 shrink-0 overflow-hidden rounded border bg-white',
              activeImage === index
                ? 'border-indigo-600 ring-1 ring-indigo-600'
                : 'border-gray-300 hover:border-indigo-400 dark:border-zinc-700',
            )}
          >
            {image(url, '', 'h-full w-full object-cover')}
          </button>
        ))}
        {images.length > THUMBS_SHOWN && (
          onShowAll ? (
            <button
              type="button"
              onClick={onShowAll}
              title="Показать все изображения"
              className="flex h-14 w-11 items-center justify-center rounded border border-dashed border-gray-300 bg-gray-100 text-[11px] font-semibold text-gray-500 hover:border-indigo-400 hover:text-indigo-600 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-400"
            >
              +{images.length - THUMBS_SHOWN}
            </button>
          ) : (
            <span className="flex h-14 w-11 items-center justify-center rounded border border-dashed border-gray-300 bg-gray-100 text-[11px] font-semibold text-gray-500 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">
              +{images.length - THUMBS_SHOWN}
            </span>
          )
        )}
      </div>
    )}
  </div>
  )

  const statsGrid = (
    <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-gray-200 bg-gray-200 dark:border-zinc-800 dark:bg-zinc-800">
      {stats.map((stat) => (
        <div
          key={stat.label}
          className={cn('min-w-0 bg-white px-2.5 py-2 dark:bg-zinc-900', stat.wide && 'col-span-2')}
        >
          <div className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-zinc-500">
            {stat.label}
          </div>
          <div
            className={cn(
              'mt-0.5 truncate text-[15px] font-semibold tabular-nums text-gray-900 dark:text-zinc-100',
              stat.tone === 'dim' && 'font-medium text-gray-400 dark:text-zinc-500',
              stat.tone === 'bad' && 'text-red-700 dark:text-red-400',
            )}
          >
            {stat.value}
          </div>
          {stat.note && (
            <div className="mt-px text-[10px] text-gray-400 dark:text-zinc-500">{stat.note}</div>
          )}
        </div>
      ))}
    </div>
  )

  const originsBlock = (
    <div>
      <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-500">
        {originsTitle}
      </div>
      <div className="flex flex-col gap-0.5">
        {origins.map((origin) => {
          const value = (
            <span className={cn('truncate font-medium', origin.mono && 'font-mono')}>
              {origin.value}
            </span>
          )
          const row = 'flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-[13px]'
          const copyButton = origin.copy ? (
            <CopyButton value={origin.value} label={origin.label.toLowerCase()} size="xs" />
          ) : null
          const interactive =
            'border border-transparent text-indigo-700 hover:border-gray-200 hover:bg-white hover:no-underline dark:text-indigo-400 dark:hover:border-zinc-700 dark:hover:bg-zinc-900'
          if (!origin.href && origin.onClick) {
            return (
              <div key={origin.label} className={cn(row, 'text-gray-700 dark:text-zinc-300')}>
                <span className="shrink-0 text-xs text-gray-500 dark:text-zinc-500">{origin.label}</span>
                <span className="flex min-w-0 items-center gap-0.5">
                  <button
                    type="button"
                    onClick={origin.onClick}
                    className={cn('min-w-0 truncate rounded px-1 font-medium', interactive, origin.mono && 'font-mono')}
                  >
                    {origin.value}
                  </button>
                  {copyButton}
                </span>
              </div>
            )
          }
          return origin.href ? (
            <a
              key={origin.label}
              href={origin.href}
              className={cn(
                row,
                'border border-transparent text-indigo-700 hover:border-gray-200 hover:bg-white hover:no-underline dark:text-indigo-400 dark:hover:border-zinc-700 dark:hover:bg-zinc-900',
              )}
            >
              <span className="shrink-0 text-xs text-gray-500 dark:text-zinc-500">
                {origin.label}
              </span>
              {value}
            </a>
          ) : (
            <div key={origin.label} className={cn(row, 'text-gray-700 dark:text-zinc-300')}>
              <span className="shrink-0 text-xs text-gray-500 dark:text-zinc-500">
                {origin.label}
              </span>
              <span className="flex min-w-0 items-center gap-0.5">
                {value}
                {copyButton}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )

  return (
    <div className="space-y-3" data-layout={narrow ? 'compact' : 'full'}>
      {narrow ? (
        <div className="flex items-start gap-3">
          {media}
          <div className="min-w-0 flex-1">{statsGrid}</div>
        </div>
      ) : (
        <>
          {media}
          {statsGrid}
        </>
      )}
      {originsBlock}
      {footer}
    </div>
  )
}

export default CardSidebar
